"use client";

import { hasPermission, type Permission, type Role } from "@gosaas/types/rbac";
import { IconChevronUp } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import React, { Fragment, useMemo, useState } from "react";
import { useTeam } from "@/components/providers/team";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	SidebarGroup,
	SidebarGroupLabel,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	useSidebar,
} from "@/components/ui/sidebar";
import {
	isMcpSurfaceEnabled,
	isModuleEnabled,
	isPublicApiSurfaceEnabled,
	type ModuleId,
} from "@/lib/modules";
import { cn } from "@/lib/utils";

type NavItem = {
	title: string;
	url: string;
	icon?: React.ElementType;
	emoji?: string;
	permissions?: string[];
	items?: NavItem[];
};

type Nav = {
	title: string;
	url?: string;
	icon?: React.ElementType;
	emoji?: string;
	isActive?: boolean;
	useCollapsible?: boolean;
	items?: NavItem[];
	permissions?: string[];
	hideForPersonal?: boolean;
	/** When set, hide this entry if the module is disabled in modules.yaml */
	module?: ModuleId;
	/** When set, hide unless public_api.mcp is enabled */
	requiresMcp?: boolean;
};

export function filterNavItemsForRole(
	role: Role | undefined,
	items: NavItem[] | undefined,
): NavItem[] {
	if (!items?.length) return [];
	const out: NavItem[] = [];
	for (const item of items) {
		if (item.permissions?.length) {
			if (!hasPermission(role as Role, item.permissions[0] as Permission)) {
				continue;
			}
		}
		if (item.items?.length) {
			const nested = filterNavItemsForRole(role, item.items);
			if (nested.length === 0) continue;
			out.push({ ...item, items: nested });
			continue;
		}
		out.push(item);
	}
	return out;
}

/** Top-level nav row: hide if user lacks permission or collapsible has no visible children. */
export function navEntryVisibleForRole(
	role: Role | undefined,
	item: Nav,
): boolean {
	if (item.permissions?.length) {
		if (!hasPermission(role as Role, item.permissions[0] as Permission)) {
			return false;
		}
	}
	if (item.useCollapsible && item.items?.length) {
		return filterNavItemsForRole(role, item.items).length > 0;
	}
	return true;
}

export function filterNavForRole(role: Role | undefined, nav: Nav[]): Nav[] {
	const result: Nav[] = [];
	for (const item of nav) {
		if (!navEntryVisibleForRole(role, item)) continue;
		if (item.useCollapsible && item.items?.length) {
			result.push({ ...item, items: filterNavItemsForRole(role, item.items) });
		} else {
			result.push(item);
		}
	}
	return result;
}

export function NavSubItems({
	items,
	state,
	isMobile,
}: {
	items: NavItem[];
	state: "expanded" | "collapsed";
	isMobile: boolean;
}) {
	return (
		<>
			{items.map((subItem) => {
				if (subItem.items && subItem.items.length > 0) {
					return (
						<Collapsible key={subItem.title} className="group/subcollapsible">
							<SidebarMenuSubItem>
								<CollapsibleTrigger className="text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground data-active:bg-sidebar-accent data-active:text-sidebar-accent-foreground h-7 gap-2 rounded-md px-2 focus-visible:ring-2 data-[size=md]:text-sm data-[size=sm]:text-xs [&_svg]:size-4 flex w-full min-w-0 -translate-x-px items-center overflow-hidden outline-hidden group-data-[collapsible=icon]:hidden disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&_svg]:shrink-0">
									{state === "collapsed" || isMobile ? (
										subItem.emoji ? (
											<span className="size-4">{subItem.emoji}</span>
										) : subItem.icon ? (
											<subItem.icon className="size-4" />
										) : null
									) : (
										<span>{subItem.title}</span>
									)}
									{state !== "collapsed" && !isMobile && (
										<IconChevronUp className="ml-auto transition-transform duration-500 ease-in-out group-data-[state=open]/subcollapsible:rotate-180" />
									)}
								</CollapsibleTrigger>
								<CollapsibleContent>
									<SidebarMenuSub>
										<NavSubItems
											items={subItem.items}
											state={state}
											isMobile={isMobile}
										/>
									</SidebarMenuSub>
								</CollapsibleContent>
							</SidebarMenuSubItem>
						</Collapsible>
					);
				}
				return (
					<SidebarMenuSubItem key={subItem.title}>
						<SidebarMenuSubButton
							render={
								<Link to={subItem.url} className="w-full">
									{state === "collapsed" || isMobile ? (
										subItem.emoji ? (
											<span className="size-4">{subItem.emoji}</span>
										) : subItem.icon ? (
											<subItem.icon className="size-4" />
										) : null
									) : (
										<span>{subItem.title}</span>
									)}
								</Link>
							}
						/>
					</SidebarMenuSubItem>
				);
			})}
		</>
	);
}

function CollapsedChildItems({
	parentItem,
	section,
	state,
	isMobile,
}: {
	parentItem: Nav;
	section?: string;
	state: "expanded" | "collapsed";
	isMobile: boolean;
}) {
	if (!parentItem.items || state !== "collapsed" || isMobile) return null;
	return (
		<>
			{parentItem.items.map((subItem) => {
				if (!subItem.url || subItem.url === "#") return null;
				if (subItem.items && subItem.items.length > 0) return null;
				const tooltipText = section
					? `${section} - ${parentItem.title} ${subItem.title}`
					: `${parentItem.title} - ${subItem.title}`;
				return (
					<SidebarMenuItem key={subItem.title}>
						<SidebarMenuButton
							{...({
								asChild: true,
								tooltip: tooltipText,
							} as unknown as React.ComponentProps<typeof SidebarMenuButton>)}
						>
							<Link to={subItem.url}>
								{subItem.emoji ? (
									<span className="size-4">{subItem.emoji}</span>
								) : subItem.icon ? (
									<subItem.icon className="size-4" />
								) : (
									<div className="size-4" />
								)}
							</Link>
						</SidebarMenuButton>
					</SidebarMenuItem>
				);
			})}
		</>
	);
}

export function NavMain({
	nav,
	title,
	icon,
	action,
}: {
	nav: Nav[];
	title: string;
	icon?: React.ElementType;
	action?: React.ReactNode;
}) {
	const { state, isMobile } = useSidebar();
	const { teamID, teams } = useTeam();

	const team = teams.find((t) => t.id === teamID);
	const role = team?.role as Role | undefined;

	const visibleNav = useMemo(() => {
		const hidePersonalItems =
			!isModuleEnabled("team") || team?.type === "personal";
		const preFiltered = hidePersonalItems
			? nav.filter((item) => !item.hideForPersonal)
			: nav;
		const moduleFiltered = preFiltered.filter((item) => {
			if (item.requiresMcp && !isMcpSurfaceEnabled()) return false;
			if (!item.module) return true;
			if (item.module === "public_api") return isPublicApiSurfaceEnabled();
			return isModuleEnabled(item.module);
		});
		return filterNavForRole(role, moduleFiltered);
	}, [nav, role, team]);

	if (visibleNav.length === 0) {
		return null;
	}

	return (
		<SidebarGroup>
			<SidebarGroupLabel className="flex items-center">
				{icon
					? React.createElement(icon as React.ElementType, {
							className: "size-4 mr-2 text-muted-foreground",
						})
					: null}
				<span className="flex-1">{title}</span>
				{action}
			</SidebarGroupLabel>
			<SidebarMenu>
				{visibleNav.map((item) => (
					<CollapsibleItem
						section={title}
						key={item.title}
						item={item}
						state={state}
						isMobile={isMobile}
					/>
				))}
			</SidebarMenu>
		</SidebarGroup>
	);
}

function CollapsibleItem({
	section,
	item,
	state,
	isMobile,
}: {
	section?: string;
	item: Nav;
	state: "expanded" | "collapsed";
	isMobile: boolean;
}) {
	const [isOpen, setIsOpen] = useState(item.isActive ?? false);

	if (!item.useCollapsible) {
		// Hide items without URLs when collapsed
		if (
			(state === "collapsed" || isMobile) &&
			(!item.url || item.url === "#")
		) {
			return null;
		}

		return (
			<SidebarMenuItem>
				<SidebarMenuButton
					{...({
						asChild: true,
						tooltip: `${section ? `${section} - ` : ""}${item.title}`,
					} as unknown as React.ComponentProps<typeof SidebarMenuButton>)}
				>
					<Link to={item.url} className="flex items-center gap-2 w-full">
						{item.emoji ? (
							<span className="size-4 shrink-0 flex items-center justify-center leading-none">
								{item.emoji}
							</span>
						) : item.icon ? (
							<item.icon className="shrink-0" />
						) : null}
						<span className="truncate">{item.title}</span>
					</Link>
				</SidebarMenuButton>
			</SidebarMenuItem>
		);
	}

	return (
		<Fragment>
			<Collapsible
				open={isOpen}
				onOpenChange={setIsOpen}
				className="group/collapsible"
			>
				<SidebarMenuItem>
					{state === "collapsed" || isMobile ? (
						// Hide items without URLs when collapsed
						!item.url || item.url === "#" ? null : (
							// Collapsed mode: just show icon link
							<SidebarMenuButton
								{...({
									asChild: true,
									tooltip: `${section ? `${section} - ` : ""}${item.title}`,
								} as unknown as React.ComponentProps<typeof SidebarMenuButton>)}
							>
								<Link
									to={item.url}
									className="flex items-center justify-center w-full"
								>
									{item.emoji ? (
										<span className="size-4">{item.emoji}</span>
									) : item.icon ? (
										<item.icon className="size-4" />
									) : null}
								</Link>
							</SidebarMenuButton>
						)
					) : (
						// Expanded mode: show full button that toggles (no navigation when has children)
						<>
							<CollapsibleTrigger className="ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground data-active:bg-sidebar-accent data-active:text-sidebar-accent-foreground data-open:hover:bg-sidebar-accent data-open:hover:text-sidebar-accent-foreground gap-2 rounded-md p-2 text-left text-sm transition-[width,height,padding] group-has-data-[sidebar=menu-action]/menu-item:pr-8 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! focus-visible:ring-2 data-active:font-medium peer/menu-button flex w-full items-center overflow-hidden outline-hidden group/menu-button disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&_svg]:size-4 [&_svg]:shrink-0 h-8 text-sm group/row">
								<div className="flex items-center gap-2 flex-1 min-w-0">
									{item.emoji ? (
										<span className="size-4 shrink-0 flex items-center justify-center leading-none">
											{item.emoji}
										</span>
									) : item.icon ? (
										<item.icon className="size-4 shrink-0" />
									) : null}
									<span className="truncate">{item.title}</span>
								</div>
								<div className="ml-auto p-1.5 hover:bg-sidebar-accent rounded-md -mr-1 transition-all opacity-60 group-hover/row:opacity-100 group-hover/row:bg-sidebar-accent/50 pointer-events-none shrink-0">
									<IconChevronUp
										className={cn(
											"size-5 transition-transform duration-200 ease-in-out",
											isOpen && "rotate-180",
										)}
									/>
								</div>
							</CollapsibleTrigger>
							<CollapsibleContent>
								<SidebarMenuSub>
									{item.items && (
										<NavSubItems
											items={item.items}
											state={state}
											isMobile={isMobile}
										/>
									)}
								</SidebarMenuSub>
							</CollapsibleContent>
						</>
					)}
				</SidebarMenuItem>
			</Collapsible>
			<CollapsedChildItems
				parentItem={item}
				section={section}
				state={state}
				isMobile={isMobile}
			/>
		</Fragment>
	);
}
